import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Container,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Tooltip,
  ListItemButton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  BarChart as BarChartIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon,
  Tune as TuneIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Mail as MailIcon,
  SupportAgent as SupportAgentIcon,
  ForumOutlined as ConversationsIcon,
  BarChartOutlined as TokenUsageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import {
  useAdminNotificationsActions,
  useAdminNotificationsSummary,
} from '../../hooks/admin/useAdminNotifications';
import { INK, FONTS } from '../../styles/ledger';
import { ledgerMenuPaperSx } from './LedgerPrimitives';

const drawerWidth = 264;
const appBarHeight = 60;

type NavGroup = {
  label: string;
  items: Array<{ name: string; icon: React.ReactNode; path: string }>;
};

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Operations',
    items: [
      { name: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
      { name: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
      { name: 'Orders', icon: <ShoppingCartIcon />, path: '/admin/orders' },
    ],
  },
  {
    label: 'Logistics',
    items: [
      { name: 'Internal shipments', icon: <LocalShippingIcon />, path: '/admin/internal-shipments' },
      { name: 'Shipment rates', icon: <TuneIcon />, path: '/admin/internal-shipment-rates' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { name: 'Commissions', icon: <BarChartIcon />, path: '/admin/commissions' },
    ],
  },
  {
    label: 'Support',
    items: [
      { name: 'Inbox', icon: <SupportAgentIcon />, path: '/admin/support' },
    ],
  },
  {
    label: 'AI agent',
    items: [
      { name: 'Conversations', icon: <ConversationsIcon />, path: '/admin/ai/conversations' },
      { name: 'Token usage', icon: <TokenUsageIcon />, path: '/admin/ai/token-usage' },
    ],
  },
];

const AdminLayout: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const notificationsSummary = useAdminNotificationsSummary();
  const notificationsActions = useAdminNotificationsActions();
  const unreadCount = notificationsSummary.data?.data.unreadCount ?? 0;
  const supportWaiting = notificationsSummary.data?.data.supportWaitingAdminCount ?? 0;
  const latestNotifications = notificationsSummary.data?.data.latest ?? [];

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [notifAnchor, setNotifAnchor] = useState<HTMLElement | null>(null);

  // Auto-close drawer on mobile route change
  React.useEffect(() => {
    if (isMobile) setDrawerOpen(false);
  }, [location.pathname, isMobile]);

  const handleLogout = () => {
    setUserMenuAnchor(null);
    logout();
    navigate('/login');
  };

  const navigateToNotification = async (n: any) => {
    try {
      if (n?.id && !n?.readAt) await notificationsActions.markRead(n.id);
    } finally {
      const adminUrl = n?.data?.admin_url || n?.data?.adminUrl;
      if (adminUrl) {
        try {
          const u = adminUrl.startsWith('http') ? new URL(adminUrl) : null;
          navigate(u ? `${u.pathname}${u.search}` : adminUrl);
        } catch {
          navigate(adminUrl);
        }
      }
      setNotifAnchor(null);
    }
  };

  // ─── SIDEBAR CONTENT ───
  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: INK.bg }}>
      {/* Brand mark */}
      <Box sx={{ px: 3, pt: 3, pb: 2.5, borderBottom: `1px solid ${INK.hairline}` }}>
        <Stack4Box>
          <Typography
            sx={{
              fontFamily: FONTS.serif,
              fontSize: 22,
              color: INK.text,
              letterSpacing: '-0.01em',
              lineHeight: 1,
            }}
          >
            Almaship
            <Box component="span" sx={{ color: INK.accent, fontStyle: 'italic' }}>
              .
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: FONTS.mono,
              fontSize: 9,
              letterSpacing: '0.22em',
              color: INK.subtle,
              textTransform: 'uppercase',
              mt: 0.5,
            }}
          >
            Admin · Ledger
          </Typography>
        </Stack4Box>
      </Box>

      {/* Operator card */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${INK.hairline}` }}>
        <Typography
          sx={{
            fontFamily: FONTS.mono,
            fontSize: 9,
            letterSpacing: '0.2em',
            color: INK.subtle,
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          Operator
        </Typography>
        <Typography sx={{ fontFamily: FONTS.sans, fontSize: 14, color: INK.text, fontWeight: 500 }}>
          {user?.first_name} {user?.last_name}
        </Typography>
        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.muted, mt: 0.25 }}>
          {user?.email}
        </Typography>
      </Box>

      {/* Nav groups */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
        {NAV_GROUPS.map((group) => (
          <Box key={group.label} sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                px: 3,
                fontFamily: FONTS.mono,
                fontSize: 9.5,
                letterSpacing: '0.2em',
                color: INK.subtle,
                textTransform: 'uppercase',
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              {group.label}
            </Typography>
            <List dense disablePadding>
              {group.items.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                return (
                  <ListItem key={item.name} disablePadding>
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      selected={isActive}
                      sx={{
                        mx: 1.5,
                        px: 1.5,
                        py: 0.85,
                        borderRadius: 0.5,
                        color: INK.muted,
                        position: 'relative',
                        '&.Mui-selected': {
                          bgcolor: INK.accentSoft,
                          color: INK.accent,
                          '& .MuiListItemIcon-root': { color: INK.accent },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: -12,
                            top: 6,
                            bottom: 6,
                            width: 2,
                            bgcolor: INK.accent,
                          },
                          '&:hover': { bgcolor: INK.accentSoft },
                        },
                        '&:hover': { bgcolor: INK.surfaceTint, color: INK.text },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32, color: 'inherit', '& svg': { fontSize: 17 } }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{
                          sx: {
                            fontFamily: FONTS.sans,
                            fontSize: 13.5,
                            fontWeight: isActive ? 600 : 500,
                            letterSpacing: '-0.005em',
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Sidebar footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${INK.hairline}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: FONTS.mono,
            fontSize: 9.5,
            letterSpacing: '0.16em',
            color: INK.subtle,
            textTransform: 'uppercase',
          }}
        >
          v.1 · {new Date().getFullYear()}
        </Typography>
      </Box>
    </Box>
  );

  return (
    // data-theme="light": the admin ledger palette is light-only, so pin the design tokens even when the app is in dark mode.
    <Box data-theme="light" sx={{ display: 'flex', minHeight: '100vh', bgcolor: INK.bg, fontFamily: FONTS.sans }}>
      {/* TOPBAR */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: INK.bg,
          color: INK.text,
          borderBottom: `1px solid ${INK.hairline}`,
          backgroundImage: 'none',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', height: appBarHeight, minHeight: `${appBarHeight}px !important`, px: { xs: 2, md: 3 } }}>
          {/* Left: brand on mobile / breadcrumb on desktop */}
          <Stack4Box row gap={1.5} align="center">
            <IconButton
              onClick={() => setDrawerOpen(!drawerOpen)}
              sx={{
                border: `1px solid ${INK.hairline}`,
                borderRadius: 0.5,
                width: 36,
                height: 36,
                color: INK.muted,
                display: { lg: 'none' },
                '&:hover': { bgcolor: INK.surfaceTint, color: INK.text },
              }}
              size="small"
            >
              {drawerOpen && isMobile ? <CloseIcon sx={{ fontSize: 18 }} /> : <MenuIcon sx={{ fontSize: 18 }} />}
            </IconButton>
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'baseline', gap: 1.5 }}>
              <Box sx={{ width: 16, height: 1, bgcolor: INK.accent }} />
              <Typography
                sx={{
                  fontFamily: FONTS.mono,
                  fontSize: 10.5,
                  letterSpacing: '0.18em',
                  color: INK.accent,
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                Admin console
              </Typography>
            </Box>
            <Box sx={{ display: { lg: 'none' } }}>
              <Typography sx={{ fontFamily: FONTS.serif, fontSize: 18, color: INK.text, lineHeight: 1 }}>
                Almaship
                <Box component="span" sx={{ color: INK.accent, fontStyle: 'italic' }}>
                  .
                </Box>
              </Typography>
            </Box>
          </Stack4Box>

          {/* Right: notifications + support + avatar */}
          <Stack4Box row gap={1} align="center">
            <Tooltip title="Notifications">
              <IconButton
                onClick={(e) => setNotifAnchor(e.currentTarget)}
                sx={topbarIconBtnSx}
                size="small"
              >
                <NotificationsIcon sx={{ fontSize: 18 }} />
                {unreadCount > 0 && <BadgeDot count={unreadCount} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Support inbox">
              <IconButton
                onClick={() => navigate('/admin/support')}
                sx={topbarIconBtnSx}
                size="small"
              >
                <MailIcon sx={{ fontSize: 18 }} />
                {supportWaiting > 0 && <BadgeDot count={supportWaiting} tone="accent" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Account">
              <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ ml: 0.5 }} size="small">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 0.5,
                    bgcolor: INK.accent,
                    color: INK.surface,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: FONTS.mono,
                    fontSize: 11.5,
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                  }}
                >
                  {(user?.first_name?.[0] || '').toUpperCase()}
                  {(user?.last_name?.[0] || '').toUpperCase()}
                </Box>
              </IconButton>
            </Tooltip>
          </Stack4Box>
        </Toolbar>
      </AppBar>

      {/* Notifications dropdown */}
      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={() => setNotifAnchor(null)}
        PaperProps={{
          sx: {
            ...ledgerMenuPaperSx,
            width: 340,
            maxHeight: 440,
            overflow: 'auto',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: `1px solid ${INK.hairlineSoft}` }}>
          <Typography sx={{ fontFamily: FONTS.serif, fontSize: 18, color: INK.text, letterSpacing: '-0.01em' }}>
            Notifications
            <Box component="span" sx={{ ml: 1, fontFamily: FONTS.mono, fontSize: 12, color: INK.muted }}>
              {String(unreadCount).padStart(2, '0')}
            </Box>
          </Typography>
          {unreadCount > 0 && (
            <Typography
              onClick={async () => {
                await notificationsActions.markAllRead();
              }}
              sx={{
                fontFamily: FONTS.mono,
                fontSize: 10,
                letterSpacing: '0.14em',
                color: INK.accent,
                textTransform: 'uppercase',
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': { color: INK.accentHover },
              }}
            >
              Mark all read
            </Typography>
          )}
        </Box>
        {notificationsSummary.isLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle, letterSpacing: '0.14em' }}>
              Loading…
            </Typography>
          </Box>
        ) : latestNotifications.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontFamily: FONTS.serif, fontSize: 18, color: INK.text, letterSpacing: '-0.01em' }}>
              All caught up.
            </Typography>
            <Typography sx={{ fontFamily: FONTS.sans, fontSize: 12.5, color: INK.muted, mt: 0.5 }}>
              No new notifications right now.
            </Typography>
          </Box>
        ) : (
          latestNotifications.map((n) => (
            <MenuItem
              key={n.id}
              onClick={() => navigateToNotification(n)}
              sx={{
                py: 1.5,
                px: 2,
                borderBottom: `1px solid ${INK.hairlineSoft}`,
                '&:hover': { bgcolor: INK.surfaceTint },
                opacity: n.readAt ? 0.6 : 1,
                position: 'relative',
                ...(n.readAt
                  ? {}
                  : {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 8,
                        top: '50%',
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        bgcolor: INK.accent,
                        transform: 'translateY(-50%)',
                      },
                    }),
              }}
            >
              <Box sx={{ pl: n.readAt ? 0 : 1.5 }}>
                <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.text, fontWeight: 600, letterSpacing: '-0.005em' }}>
                  {n.title || 'Notification'}
                </Typography>
                {n.body && (
                  <Typography sx={{ fontFamily: FONTS.sans, fontSize: 12, color: INK.muted, mt: 0.25 }}>
                    {n.body}
                  </Typography>
                )}
                <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.subtle, mt: 0.5, letterSpacing: '0.04em' }}>
                  {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
        <Box
          onClick={() => {
            setNotifAnchor(null);
            navigate('/admin/support');
          }}
          sx={{
            py: 1.25,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: INK.surfaceTint,
            '&:hover': { bgcolor: INK.accentSoft },
          }}
        >
          <Typography
            sx={{
              fontFamily: FONTS.mono,
              fontSize: 10.5,
              letterSpacing: '0.18em',
              color: INK.accent,
              textTransform: 'uppercase',
              fontWeight: 500,
            }}
          >
            Open support inbox →
          </Typography>
        </Box>
      </Menu>

      {/* User menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        PaperProps={{ sx: { ...ledgerMenuPaperSx, minWidth: 220 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 2, borderBottom: `1px solid ${INK.hairlineSoft}` }}>
          <Typography sx={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, color: INK.text }}>
            {user?.first_name} {user?.last_name}
          </Typography>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.muted, mt: 0.25 }}>
            {user?.email}
          </Typography>
        </Box>
        <MenuItem
          onClick={() => {
            setUserMenuAnchor(null);
            navigate('/profile');
          }}
        >
          <ListItemIcon>
            <PersonIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </Menu>

      {/* SIDEBAR */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            borderRight: `1px solid ${INK.hairline}`,
            backgroundImage: 'none',
          },
        }}
      >
        <Toolbar sx={{ minHeight: `${appBarHeight}px !important`, height: appBarHeight }} />
        {sidebarContent}
      </Drawer>

      {/* MAIN CONTENT */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: `${appBarHeight}px`,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: INK.bg,
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: 3,
            px: { xs: 2, sm: 3, md: 4 },
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

/* ─── tiny helper components / sx ─── */

const topbarIconBtnSx = {
  border: `1px solid ${INK.hairline}`,
  borderRadius: 0.5,
  width: 36,
  height: 36,
  color: INK.muted,
  position: 'relative' as const,
  '&:hover': { bgcolor: INK.surfaceTint, borderColor: INK.text, color: INK.text },
};

const BadgeDot: React.FC<{ count: number; tone?: 'accent' | 'danger' }> = ({ count, tone = 'danger' }) => (
  <Box
    sx={{
      position: 'absolute',
      top: -3,
      right: -3,
      minWidth: 15,
      height: 15,
      bgcolor: tone === 'accent' ? INK.accent : INK.inactive,
      color: INK.surface,
      borderRadius: 0.5,
      fontFamily: FONTS.mono,
      fontSize: 8.5,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 0.35,
      border: `1px solid ${INK.bg}`,
      letterSpacing: '0.02em',
    }}
  >
    {count > 99 ? '99+' : count}
  </Box>
);

// Lightweight Stack alternative — saves importing Stack everywhere
const Stack4Box: React.FC<{
  children: React.ReactNode;
  row?: boolean;
  gap?: number;
  align?: 'flex-start' | 'center' | 'flex-end' | 'stretch';
}> = ({ children, row, gap = 0, align }) => (
  <Box sx={{ display: 'flex', flexDirection: row ? 'row' : 'column', gap, alignItems: align }}>{children}</Box>
);

export default AdminLayout;
